/**
 * useChatBotPendingCount — polls the unanswered-questions count so the
 * admin sidebar can show a red badge on the Chat Bot tab.
 *
 * Refresh: on mount, then every 60s while the tab is visible. Silent on
 * error (returns 0 rather than crashing the whole sidebar).
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";

export default function useChatBotPendingCount(enabled = true) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let intervalId = null;
    const fetchCount = async () => {
      try {
        const { data } = await API.get("/admin/chat-bot/pending-review-count?days=7");
        if (!cancelled) setCount(Number(data?.count || 0));
      } catch {
        if (!cancelled) setCount(0);
      }
    };
    fetchCount();
    intervalId = setInterval(fetchCount, 60_000);
    // Manual refresh hook — any component that just resolved an
    // unanswered question (e.g. ChatBotTab's Teach form) can fire
    // `window.dispatchEvent(new Event("chatbot-pending-refresh"))`
    // to make the badge drop immediately instead of waiting 60s.
    const onManualRefresh = () => fetchCount();
    window.addEventListener("chatbot-pending-refresh", onManualRefresh);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener("chatbot-pending-refresh", onManualRefresh);
    };
  }, [enabled]);

  return count;
}
